import { format } from 'date-fns';
import { Link } from 'react-router-dom';
import React from 'react';
import PropTypes from 'prop-types';
import styled, { css, keyframes } from 'styled-components';

import PrivateRoute from 'utils/privateRoute';

import Icon from '../components/base/Icon';

import Navigation, { NavigationMobile } from './ProjectLayout/Navigation';
import Userbar from './ProjectLayout/Userbar';

import Queries from 'graphql/Queries';

const Wrapper = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  transition: 100ms grid-template-columns var(--quintOut);
  @media (min-width: 50em) {
    grid-template-columns: 8.5rem 1fr;
    ${props => props.wideSidebar && css`
      grid-template-columns: 30% 1fr;
    `}
  }
  @media (min-width: 69em) {
    ${props => props.wideSidebar && css`
      grid-template-columns: 18rem 1fr;
    `}
  }

  @supports not (display: grid) {
    display: flex;
    flex-direction: column;
    @media (min-width: 50em) {
      flex-direction: row;
    }
  }
`;

const Main = styled.div`
  display: grid;
  grid-column-gap: 1rem;
  grid-row-gap: 1.125rem;
  grid-template-columns: 1fr;
  margin: 0 auto auto;
  max-width: 80rem;
  padding: 1rem;
  width: 100%;
  @media (min-width: 50em) {
    grid-column-gap: 1.5rem;
    grid-row-gap: 1.25rem;
    padding: 0 1.5rem 1.5rem 1.5rem;
  }
  @media (min-width: 63em) {
    grid-gap: 2.375rem;
    padding: 0 2.625rem 2.625rem 2.625rem;
  }
  @media (min-width: 79em) {
    grid-template-columns: auto 17rem;
  }

  @supports not (display: grid) {
    > div,
    > h1 {
      margin-bottom: 2.625rem;
    }
  }
`;

// Page

export const Page = styled(({ defaultDashboard, onboarding, noPadding, updates, list, ...rest }) => <div {...rest} />)`
  background: var(--white);
  box-shadow: 0 0 0.625rem 0 rgba(126, 124, 151, 0.25);
  grid-column: 1 / span 1;
  overflow: hidden;
  padding: 1rem;

  ${props => props.onboarding && css`
    display: grid;
    grid-template-columns: 1fr;
    padding: 0;
  `}
  ${props => props.defaultDashboard && css`
    grid-column-end: span 1;
    background: transparent;
    box-shadow: none;
    padding: 0;
    overflow: initial;
  `}

  ${props => props.list && css`
    padding-bottom: 0.25rem;
    padding-top: 0.25rem;
  `}
  ${props => props.updates && css`
    padding-top: 1rem;
    padding-bottom: 1rem;
  `}
  ${props => props.noPadding && css`
    padding: 0;
  `}

  @media (min-width: 35em) {
    // default padding
    ${props =>
    !(props.onboarding ||
    props.updates ||
    props.noPadding ||
    props.defaultDashboard) && css`
      padding: 1.5rem;
    `}
  }
  @media (min-width: 44em) {
    ${props => props.defaultDashboard && css`
      background: var(--white);
      box-shadow: 0 0 0.625rem 0 rgba(126, 124, 151, 0.25);
      padding: 1.5rem;
      overflow: hidden;
    `}
  }
  @media (min-width: 63em) {
    // default padding
    ${props =>
    !(props.onboarding ||
    props.updates ||
    props.noPadding) && css`
      padding: 3rem;
    `}
  }
  @media (min-width: 79em) {
    grid-column-end: span 2;
    ${props => props.onboarding && css`
      grid-template-columns: 33% 1fr;
    `}
    ${props => props.defaultDashboard && css`
      grid-column-end: span 1;
    `}
  }
  @media (min-width: 88em) {
    ${props => props.onboarding && css`
      grid-template-columns: 25% 1fr;
    `}
  }
`;

Page.defaultProps = {
  defaultDashboard: false,
  list: false,
  noPadding: false,
  onboarding: false,
  updates: false,
};

Page.propTypes = {
  defaultDashboard: PropTypes.bool,
  list: PropTypes.bool,
  noPadding: PropTypes.bool,
  onboarding: PropTypes.bool,
  updates: PropTypes.bool,
};

export const PageTitle = styled.h1`
  align-items: center;
  display: flex;
  font-size: var(--step-up-3);
  flex-wrap: wrap;
  margin: 0.25rem 0.5rem 0 0;
  @media (min-width: 63em) {
    margin: 0.8125rem 0.5rem -0.5625rem 0;
  }
`;

export const PageTitleArea = styled.div`
  align-items: center;
  display: block;
  grid-column: 1 / span 1;
  justify-content: flex-start;
  margin: 0;
  width: 100%;

  a {
    margin: 0;
    width: 100%;
    @media (min-width: 25em) {
      margin: initial;
      width: initial;
    }
  }
  h1 {
    animation: none;
  }

  @media (min-width: 25em) {
    display: flex;
    flex-wrap: wrap;
    width: auto;
  }
  @media (min-width: 79em) {
    grid-column-end: span 2;
  }
`;

export const PageTitleAreaButton = styled.div`
  margin: 1px 0 1px auto;

  a {
    align-items: start;
    width: 100%;
  }
  svg {
    margin-top: -1px;
  }

  @media (min-width: 25em) {
    a {
      width: initial;
    }
  }
`;

export const PageTitleAreaSearch = styled.div`
  margin: auto 0 0 auto;

  div {
    margin-bottom: 0;
  }
  input {
    width: 100%;
  }

  @media (min-width: 25em) {
    input {
      width: auto;
    }
  }
`;

export const PageSubTitle = styled.div`
  align-items: flex-start;
  display: flex;
  margin: auto 0.5rem 1.125rem 0;

  div {
    display: flex;
  }
  h6 {
    margin: auto 0.5rem -0.875rem 0;
    @media (min-width: 63em) {
      margin-bottom: -1.375rem;
    }
  }
  svg {
    color: var(--red);
    height: 1.25rem;
    margin: auto 0 auto 0;
    width: 1.25rem;
  }
`;

const enterArchiveMessage = keyframes`
  0% {
    transform: translateY(-1rem);
  }
`;

// Archive

const ArchiveMessage = styled.div`
  animation: 700ms ${enterArchiveMessage} var(--quintOut);
  background: var(--red);
  color: var(--white);
  font-size: var(--step-down-1);
  padding: 0.875rem 1rem 0.625rem;
  a {
    color: inherit;
    text-decoration: underline;
    transition: 400ms opacity var(--quintOut);
    &:hover {
      opacity: 0.75;
    }
  }
  p {
    display: inline;
  }
  span {
    font-weight: bold;
    text-transform: uppercase;
    &::after {
      content: '\\2022';
      display: inline;
      margin: 0 0.75rem;
    }
  }
  svg {
    vertical-align: -0.0625rem;
    margin-right: 0.5rem;
  }
  @media (min-width: 50em) {
    padding-left: 1.375rem;
    padding-right: 1.375rem;
  }
`;

class UserbarWrapper extends React.Component {
  state = {
    menuActive: false,
  };

  constructor(props) {
    super(props);

    this._handleMenuClick = this._handleMenuClick.bind(this);
  }

  _handleMenuClick() {
    this.setState({ menuActive: !this.state.menuActive });
    return true;
  }

  render() {
    const { projectId, setSidebarWidth } = this.props;
    return (
      <React.Fragment>
        <NavigationMobile projectId={projectId} active={this.state.menuActive} _handleMenuClick={this._handleMenuClick} />
        <Userbar
          projectId={projectId}
          _handleMenuClick={this._handleMenuClick}
          setSidebarWidth={setSidebarWidth}
        />
      </React.Fragment>
    );
  }
}

class ProjectLayout extends React.Component {
  state = {
    wideSidebar: false,
  };

  render() {
    const {
      component: Component,
      computedMatch: {
        params: { id },
      },
      ...rest
    } = this.props;
    const { wideSidebar } = this.state;

    return (
      <React.Fragment>
        <Queries.Project variables={{ id: id }} loadingState="blank">
          {(project) => (
            <React.Fragment>
            {project.isArchived &&
              <ArchiveMessage>
                <Icon id="archive" title="archive" />
                <p>
                  <span>Archived</span>
                  This project was archived on {format(project.archivedAt, 'MMMM D, YYYY')}. <Link to={`/project/${project.id}/settings/archive`}>Click here to&nbsp;unarchive</Link>
                </p>.
              </ArchiveMessage>
            }
            </React.Fragment>
          )}
        </Queries.Project>
        <Wrapper wideSidebar={wideSidebar}>
          <UserbarWrapper projectId={id} setSidebarWidth={(wide) => { if (this.state.wideSidebar !== wide) { this.setState({ wideSidebar: wide }) } }}/>
          <Main>
            <Navigation projectId={id} location='layout' />
            <PrivateRoute
              {...rest}
              component={matchProps => (
                <Component {...matchProps} projectId={id} />
              )}
            />
          </Main>
        </Wrapper>
      </React.Fragment>
    );
  }
}

export default ProjectLayout;



// WEBPACK FOOTER //
// ./src/layouts/ProjectLayout.js